// =====================================================================
// TIER 2 — biography decode. Pure function of (envelope, personId, locale).
// Relational facts are READ from the envelope; only narrative texture is
// decoded from the person's own address hash. Locale only ever selects
// which pre-written template text gets picked — it never changes which
// template index/rng draw is picked, so the same seed narrates the same
// underlying life in either language.
//
// Migration (§11) means a person's relational facts can now legitimately
// live in TWO envelopes: her own origin village (birth, natal family) and,
// if she emigrated, the destination village that actually recorded her
// marriage. decodePerson always receives the envelope that owns the
// person's adult life (their residence), and reaches DOWN into a lower-rank
// origin envelope only to read — never resolve or invent — her parents.
// That one-directional read is safe by the same rank argument as
// pullImmigrantBride in village.ts: an origin village is never asked to
// know about a destination it hasn't been told about.
// =====================================================================
import type { Locale } from "../i18n/locale.js";
import { decodeOccupation } from "./biographyOccupation.js";
import { addPapalRefs } from "./biographyPapalRefs.js";
import { addRoyalRefs } from "./biographyRoyalRefs.js";
import { incumbencyIndexAt, institutionsBetween, parishClergyOf } from "./clergy.js";
import { CLASS_INFO } from "./data/classes.js";
import { epidemicAt, epidemicNews } from "./data/epidemics.js";
import { SAINTS } from "./data/jurisdictions.js";
import {
  ADULT_EVENTS,
  CAUSE_LABEL,
  CHILD_ACCIDENT_DETAIL,
  CHILD_EVENTS,
  COURT_CAUSES,
  DEATH_DETAIL,
  FEMALE_EVENTS,
  OLD_EVENTS,
  RISK_DEATH_DETAIL,
  SRC,
  WORLD_EVENTS,
  YOUTH_EVENTS,
} from "./data/narrative.js";
import { placeOf, placeShortOf } from "./data/placeNames.js";
import { PLAGUES, plagueAt } from "./data/plagues.js";
import { REGIONS } from "./data/regions.js";
import { citeDocument } from "./documents.js";
import { outbreakAt } from "./epidemics.js";
import { fatherOccupation } from "./fatherOccupation.js";
import { FAMINE, harvestAt, namedDearthAt, POOR_HARVEST } from "./harvest.js";
import { makeRng, personStream } from "./hash.js";
import { manorOf, parishMotherVillageIdx, parishOf } from "./hierarchy.js";
import { findResidenceRecord } from "./identity.js";
import { parentsOf } from "./lineage.js";
import { famineAt, registerBlackoutAt, warAt } from "./mortality.js";
import { lordOfManorAt, manorLineOf, royalWorldEvents, sovereignAt, tenureIndexAt } from "./nobility.js";
import { papalWorldEvents, popeIndexAt, popeTermAt } from "./papacy.js";
import { coupleMarriageDate, personBirthDate, personDeathDate } from "./season.js";
import { childrenOf, heirOf, inheritedFromFather, isFirstBornSon } from "./succession.js";
import type {
  Address,
  Bio,
  BioEvent,
  BioEventKind,
  Couple,
  DocumentKind,
  Envelope,
  EventRef,
  MedievalDate,
  Person,
  PersonAddress,
  Sex,
  SpouseRef,
  UnionRef,
} from "./types.js";
import { resolveVillage } from "./village.js";

// Resolves a `{{masc/fem}}` token to whichever half matches `sex` — the one
// piece of grammar Catalan narrative text needs that English text doesn't
// (gendered past-participle/adjective agreement with the subject).
function gender(text: string, sex: Sex): string {
  return text.replace(/\{\{([^/{}]*)\/([^/{}]*)\}\}/g, (_, m: string, f: string) => (sex === "M" ? m : f));
}

const CHILD_DEATH_CAUSE: Record<Locale, string[]> = {
  en: ["the smallpox", "the flux", "a fever", "the measles"],
  ca: ["la verola", "el flux", "una febre", "el xarampió"],
};
const WAR_ACTION: Record<Locale, string[]> = {
  en: [
    "requisitioned the grain and drove off the livestock",
    "burned two farms at the parish edge",
    "were billeted in the village for a winter, eating it bare",
    "demanded a ransom, paid in silver and salted meat",
  ],
  ca: [
    "van requisar el gra i es van endur el bestiar",
    "van cremar dues masies al límit de la parròquia",
    "es van allotjar al poble tot un hivern, deixant-lo escurat",
    "van exigir un rescat, pagat en plata i carn salada",
  ],
};
const BURIAL_PLAIN: Record<Locale, string[]> = {
  en: [
    "Buried in the churchyard among kin, with the customary mortuary paid to the priest — the second-best beast.",
    "Buried in the churchyard on the south side, with a wooden cross that would not outlast a generation.",
  ],
  ca: [
    "Enterrat{{/da}} al cementiri entre parents, amb la mortuòria acostumada pagada al capellà — la segona millor bèstia.",
    "Enterrat{{/da}} al cementiri, al costat sud, amb una creu de fusta que no duraria una generació.",
  ],
};

// Cheap, non-recursive: just the deterministic place-name formula, never a
// full village solve. Safe to call for a destination address regardless of
// rank, which is what lets the origin side name where someone went without
// depending on that village ever being resolved.
function placeNameOf(worldSeed: number, addr: Address | null | undefined, locale: Locale): string | null {
  if (!addr) return null;
  return placeOf(worldSeed, addr.regionKey, addr.villageIdx)[locale];
}

function addrOnly(a: Address): Address {
  return { regionKey: a.regionKey, villageIdx: a.villageIdx };
}

// § name links: a reference for the UI layer to turn a person's name — as
// it literally appears in an event's own text — into a link to their
// record. `full` uses "Name Surname" (most mentions); pass full=false for
// the handful of spots (children, a birth event's mother) that name someone
// by first name only.
function nameRef(person: Person, addr: Address, full = true): EventRef {
  return { id: person.id, name: full ? `${person.name} ${person.surname}` : person.name, addr: addrOnly(addr) };
}

function spouseRefOf(s: Person, addr: Address, worldSeed: number, locale: Locale): SpouseRef {
  return {
    id: s.id,
    name: s.name + " " + s.surname,
    birth: s.birth,
    death: s.death,
    incomer: !!s.incomer,
    addr: addrOnly(addr),
    originPlace: s.incomer && s.origin ? placeNameOf(worldSeed, s.origin, locale) : null,
  };
}

function unionRefOf(c: Couple, s: Person, kids: Person[], addr: Address, worldSeed: number, locale: Locale): UnionRef {
  return {
    spouse: spouseRefOf(s, addr, worldSeed, locale),
    year: c.year,
    children: kids.map((k) => ({ id: k.id, name: k.name, sex: k.sex, birth: k.birth, death: k.death, addr: addrOnly(addr) })),
  };
}

export function decodePerson(env: Envelope, id: number, locale: Locale): Bio | null {
  const p = env.persons[id];
  if (!p) return null;
  const region = env.region;
  const pHash = personStream(env.vHash, 40000, id);
  const rng = makeRng(pHash);
  const wealth = CLASS_INFO[p.cls].wealth;
  const pos = p.sex === "M" ? "his" : "her";
  const obj = p.sex === "M" ? "him" : "her";
  const selfAddr: Address = { regionKey: env.regionKey, villageIdx: env.villageIdx };
  const ca = locale === "ca";

  // § pure decode: derived narrative facts accumulate in locals and are
  // returned on the Bio — the envelope's Person records are never written.
  let literate = false;
  let occupation: string | null = null;

  // Overlapping hierarchies (§10): independent of the civil village tree,
  // resolved fresh here — cheap, non-recursive, no memo needed.
  const jurisdiction = parishOf(env.worldSeed, env.regionKey, env.villageIdx, locale);
  const fief = manorOf(env.worldSeed, env.regionKey, env.villageIdx, locale);
  const cite = (kind: DocumentKind) => citeDocument(kind, { jurisdiction, fief, place: env.place[locale] }, locale);
  // § shared parish: this village has no church of its own — the block's
  // mother parish (hierarchy.ts's parishOf) covers several villages at
  // once, so birth/burial entries can name the walk to it.
  const motherVillage = jurisdiction.shared ? placeShortOf(env.worldSeed, env.regionKey, parishMotherVillageIdx(env.villageIdx)) : null;

  // --- family lookups (all from the envelope; symmetric by construction) ---
  // An immigrant's local record has no local parents (father/mother = -1),
  // but a real origin pointer resolves to the lower-rank village that DOES —
  // read it, don't invent it. parentsOf (lineage.ts) does this same
  // resolution for the multi-generation ancestorsOf/descendantsOf traversal,
  // so it lives there rather than being duplicated here.
  const { father: fatherRec, mother: motherRec } = parentsOf(env, id);
  const father: Person | null = fatherRec?.person ?? null;
  const mother: Person | null = motherRec?.person ?? null;
  const fatherAddr = fatherRec && fatherRec.env !== env ? addrOnly(fatherRec.env) : selfAddr;
  const motherAddr = motherRec && motherRec.env !== env ? addrOnly(motherRec.env) : selfAddr;
  const fatherEnvForOcc: Envelope | null = fatherRec ? fatherRec.env : null;
  const fatherIdForOcc: number | null = fatherRec ? fatherRec.person.id : null;
  const fatherOccText = fatherEnvForOcc && fatherIdForOcc != null ? fatherOccupation(fatherEnvForOcc, fatherIdForOcc, locale) : null;

  // Natal couple: with remarriage a father can head several couples, so
  // find the one whose wife is this person's own mother.
  // § illegitimacy/legitimation: an unlegitimated natural child belongs to
  // no Couple of her parents', even if they happen to marry EACH OTHER
  // later for unrelated reasons (the ordinary market, not village.ts's own
  // immediate legitimation check) — without this guard the lookup below
  // would find that unrelated marriage by pure (father, mother) id match and
  // wrongly hand her its actual children as "siblings" (snapshot.ts's
  // orphan-grouping has the identical guard, for the identical reason).
  const natalIdx =
    p.father >= 0 && !(p.illegitimate && !p.legitimated) ? (env.persons[p.father].unions ?? []).find((ci) => env.couples[ci].wife === p.mother) : undefined;
  const natal = natalIdx != null ? env.couples[natalIdx] : null;
  const siblings = natal ? natal.children.filter((cid) => cid !== id).map((cid) => env.persons[cid]) : [];

  // Marital history: every union, in order. `own`/`spouse` keep pointing at
  // the FIRST marriage for the sections that predate remarriage support.
  const unionCouples: Couple[] = (p.unions ?? []).map((ci) => env.couples[ci]);
  const spouseOf = (c: Couple): Person => env.persons[p.id === c.husband ? c.wife : c.husband];
  const own = unionCouples[0] ?? null;
  const spouse = own ? spouseOf(own) : null;
  // § illegitimacy: childrenOf (succession.ts) is the union-based flatMap
  // above PLUS any natural child found by a direct father/mother scan — used
  // here (rather than recomputing unionCouples.flatMap by hand) so lineage.ts's
  // descendantsOf, which also calls childrenOf, never disagrees with this Bio
  // about who counts as this person's child.
  const children = childrenOf(env, id);
  // § named wills: an eligible heir (succession.ts's own tenancy-succession
  // pick) is only worth dictating a will over for someone who actually had
  // wealth to leave — the poor died intestate in practice, their few goods
  // passing by custom, not by document.
  const heir = wealth >= 4 ? heirOf(env, id) : null;
  // § occupation marital gate: an occupation entry that presupposes she's
  // married (or widowed) must only be eligible if that's actually true of
  // her own recorded life — occupation is decided at a fixed early age
  // (13), before marriage in every region, so it can only look at whether
  // these things EVER happen across her whole recorded history, not "yet".
  const everMarried = unionCouples.length > 0;
  const widowedUnions = unionCouples.filter((c) => spouseOf(c).death.year < p.death.year);

  // § canonical identity: an emigrant decoded at her ORIGIN village may have
  // a real residence record in the destination register — resolve it (bounded:
  // local cluster only) so her origin record links to her married life there.
  let destRecord: PersonAddress | null = null;
  let destEnv: Envelope | null = null;
  if (p.emigrated && p.emigrateTo && !p.longDistance && unionCouples.length === 0) {
    destRecord = findResidenceRecord(env.worldSeed, { regionKey: env.regionKey, villageIdx: env.villageIdx, personId: id }, p.emigrateTo);
    if (destRecord) destEnv = resolveVillage(env.worldSeed, destRecord.regionKey, destRecord.villageIdx);
  }
  const destPerson = destRecord && destEnv ? destEnv.persons[destRecord.personId] : null;
  const destUnion = destPerson?.unions?.length && destEnv ? destEnv.couples[destPerson.unions[0]] : null;
  const destSpouse = destUnion && destEnv ? destEnv.persons[destUnion.husband === destPerson?.id ? destUnion.wife : destUnion.husband] : null;
  // childrenOf (not destUnion.children directly), for the same reason as
  // `children` above: it also surfaces any § illegitimate child of the
  // destination record, which descendantsOf (lineage.ts) already does.
  const destChildren = destUnion && destEnv && destPerson ? childrenOf(destEnv, destPerson.id) : [];

  // § departure: once she marries out or he leaves for good, this register
  // has nothing further true to say about their local life — computed HERE
  // (before any content-producing block runs, not just the ones textually
  // after the Marriage section that narrates it) because earlier blocks
  // — plague passages, manorial accounts — are just as much a local-presence
  // claim as anything below and need the same boundary. Every localized
  // block downstream reads this as an upper bound on when its content can
  // occur, same role p.death.year already plays for ev() itself, just
  // narrower. The Marriage section below still fires the actual departure
  // event/text at exactly this year.
  // § the marriage squeeze: a departure for service in a town is dated to the
  // departure itself, never to a marriage — she left to work, and whether she
  // married at the far end is the destination register's fact, not this one's.
  const departureYear: number | null =
    !own && p.marriedOut
      ? (destUnion?.year ?? p.birth + region.marriageF[1])
      : !own && p.cityService
        ? // she went to work, then (sometimes) married there — never the
          // reverse, so a destination marriage the far register happens to
          // date early still can't precede her leaving.
          Math.min(p.birth + region.marriageF[1], destUnion?.year ?? Number.POSITIVE_INFINITY)
        : !own && p.sex === "M" && p.emigrated
          ? (destUnion?.year ?? p.birth + region.marriageM[1])
          : null;

  const events: BioEvent[] = [];
  // § register blackout: years the crisis-year check below actually
  // suppressed a texture entry — surfaced afterward as one explicit line
  // rather than leaving the gap unexplained (see the texture() loop and
  // the summary note pushed after it).
  const blackoutYears = new Set<number>();
  const ev = (year: number, text: string, kind: BioEventKind, src?: string, refs?: EventRef[], date?: MedievalDate) => {
    if (year <= p.death.year)
      events.push({ year, age: year - p.birth, text: gender(text, p.sex), kind, src: src || cite("reg"), refs: refs?.length ? refs : undefined, date });
  };

  // § the season: the three kinds of entry a parish register actually
  // dated to the day — a baptism, a wedding, a burial. Everything else
  // (world news, texture, a court roll's business) keeps the bare year,
  // which is honest: those are not register entries and were not dated
  // like them.
  //
  // Every one of these comes off its own side stream, so none of them
  // moved a single draw in the decode above. The marriage date is keyed
  // on the COUPLE index rather than on either spouse, which is what makes
  // husband and wife necessarily agree on their wedding day.
  const birthDate = personBirthDate(env.vHash, p);
  const deathDate = personDeathDate(env.vHash, p);
  const marriageDate = (coupleIdx: number, year: number, e = env): MedievalDate => coupleMarriageDate(e.vHash, coupleIdx, year);
  /** Another person's baptism day — a child's, read from their own address
   * exactly as their own record would compute it. */
  const birthDateOfPerson = (person: Person, e = env): MedievalDate | undefined => personBirthDate(e.vHash, person) ?? undefined;

  // Birth
  const bornPlague = plagueAt(p.birth, env.regionKey);
  const bNote = ca
    ? bornPlague
      ? ` — {{nascut/nascuda}} en any de pesta, ${bornPlague[3].ca}`
      : famineAt(p.birth, region)
        ? " — {{nascut/nascuda}} enmig de la fam, quan les collites havien fallat"
        : ""
    : bornPlague
      ? " — born in a plague year, " + bornPlague[3].en
      : famineAt(p.birth, region)
        ? " — born amid famine, when the harvests had failed"
        : "";
  if (p.incomer && !p.founder && p.origin) {
    ev(
      p.birth,
      ca
        ? `Va néixer a ${placeNameOf(env.worldSeed, p.origin, locale)}; la seva família consta al registre d'aquell poble. Entra en aquest amb el seu casament.`
        : `Born in ${placeNameOf(env.worldSeed, p.origin, locale)}; her people are entered in that village's own register. She comes into this one on her marriage.`,
      "birth",
      cite("reg"),
    );
  } else if (p.incomer && !p.founder) {
    ev(
      p.birth,
      ca
        ? `Va néixer a la parròquia veïna; la seva família consta en un altre registre. Entra en aquest amb el seu casament.`
        : `Born in the next parish; her people are entered in another register. She comes into this one on her marriage.`,
      "birth",
      cite("reg"),
    );
  } else if (p.founder) {
    ev(
      p.birth,
      ca
        ? `Va néixer abans que comencés aquest registre; ${p.name} hi apareix als primers folis ja {{crescut/crescuda}}, com {{un/una}} de les cases fundadores de ${env.place[locale]}.`
        : `Born before this register begins; ${p.name} appears in its first folios already grown, one of the founding households of ${env.place[locale]}.`,
      "birth",
      cite("reg"),
    );
  } else if (p.illegitimate) {
    // § illegitimacy: both parents are still real and present (village.ts
    // never assigns an unknown father — see rollIllegitimateBirths), but
    // the register's own record of the fact would be a court roll, not a
    // parish birth entry — the same "not everything got the reg citation"
    // logic as the merchet/wardship events elsewhere in this file.
    ev(
      p.birth,
      ca
        ? `Va néixer a ${env.place[locale]}, ${region.name.ca}, {{fill/filla}} natural de ${mother!.name} ${mother!.surname} i de ${father!.name}, que mai no es van casar.`
        : `Born in ${env.place[locale]}, ${region.name.en}, natural {{son/daughter}} of ${mother!.name} ${mother!.surname} and ${father!.name}, who never married her.`,
      "birth",
      cite("court"),
      [nameRef(mother!, selfAddr), nameRef(father!, selfAddr, false)],
      birthDate ?? undefined,
    );
    // § legitimation: her parents married each other after all (village.ts's
    // rollIllegitimateBirths) — canon law and most secular custom treated
    // her as legitimate from that day (per subsequens matrimonium), EXCEPT
    // in England, where the Statute of Merton (1236) had English common law
    // refuse to recognize it for inheritance — she'd still be reckoned
    // legitimate by the Church, but never by the manor court that decided
    // who took the holding.
    if (p.legitimated && p.legitimatedYear != null) {
      ev(
        p.legitimatedYear,
        env.regionKey === "england"
          ? ca
            ? `El seu pare i la seva mare es van casar l'any ${p.legitimatedYear}. L'Església la tenia per legítima des d'aquell dia — però la cort del senyor, no: la llei anglesa no reconeixia cap dret d'herència a un fill nascut abans de les noces.`
            : `Her parents married in ${p.legitimatedYear}. The Church accounted her legitimate from that day — the manor court did not: English law recognized no inheritance right in a child born before the wedding.`
          : ca
            ? `El seu pare i la seva mare es van casar l'any ${p.legitimatedYear}, i des d'aquell dia va ser tinguda per legítima, com qualsevol altre fill del matrimoni.`
            : `Her parents married in ${p.legitimatedYear}, and from that day she was accounted legitimate, fit to inherit like any other child of the marriage.`,
        "fortune",
        cite("court"),
        [nameRef(father!, selfAddr), nameRef(mother!, selfAddr, false)],
      );
    }
  } else {
    // Native-born, non-founder: both parents are guaranteed present by
    // construction (only founders/fabricated incomers have father/mother -1).
    const twin = p.twinOf != null ? env.persons[p.twinOf] : null;
    const twinNote = twin ? (ca ? ` Va néixer {{bessó/bessona}}, junt amb ${twin.name}.` : ` Born a twin, alongside ${twin.name}.`) : "";
    const sharedNote = motherVillage
      ? ca
        ? ` {{Batejat/Batejada}} a ${motherVillage}, on la parròquia mare rep els fills d'uns quants pobles del voltant.`
        : ` Christened at ${motherVillage}, whose mother church takes in the children of several villages roundabout.`
      : "";
    ev(
      p.birth,
      ca
        ? `Va néixer a ${env.place[locale]}, ${region.name.ca}, ${p.sex === "M" ? "fill" : "filla"} de ${father!.name} ${father!.surname} i ${mother!.name}${bNote}.${twinNote}${sharedNote}`
        : `Born in ${env.place[locale]}, ${region.name.en}, ${p.sex === "M" ? "son" : "daughter"} of ${father!.name} ${father!.surname} and ${mother!.name}${bNote}.${twinNote}${sharedNote}`,
      "birth",
      cite("reg"),
      twin
        ? [nameRef(father!, selfAddr), nameRef(mother!, selfAddr, false), nameRef(twin, selfAddr, false)]
        : [nameRef(father!, selfAddr), nameRef(mother!, selfAddr, false)],
      birthDate ?? undefined,
    );
  }

  // § godparents: baptismal sponsors, drawn from other adults already on
  // record in the SAME register the birth itself belongs to — the person's
  // own envelope for a native, or the real origin envelope for an immigrant
  // (the same one parentsOf resolved father/mother from above) — never
  // invented out of thin air, and skipped for a founder (whose baptism
  // predates the register) or a fabricated incomer with no addressable origin.
  if (fatherEnvForOcc) {
    const baptSelfId = fatherEnvForOcc !== env && p.originId != null ? p.originId : id;
    const pool = fatherEnvForOcc.persons.filter(
      (q) => q.id !== baptSelfId && q.id !== father?.id && q.id !== mother?.id && q.birth + 16 <= p.birth && q.death.year > p.birth,
    );
    const godfathers = pool.filter((q) => q.sex === "M");
    const godmothers = pool.filter((q) => q.sex === "F");
    if (godfathers.length && godmothers.length) {
      const gf = rng.pick(godfathers);
      const gm = rng.pick(godmothers);
      ev(
        p.birth,
        ca
          ? `Als peus de la pica, ${gf.name} ${gf.surname} i ${gm.name} ${gm.surname} en van ser els padrins, i van respondre per {{ell/ella}} davant el capellà.`
          : `At the font, ${gf.name} ${gf.surname} and ${gm.name} ${gm.surname} stood godparents, and answered for ${obj} before the priest.`,
        "life",
        undefined,
        [nameRef(gf, fatherEnvForOcc), nameRef(gm, fatherEnvForOcc)],
      );
    }
  }

  // Plague passages: family losses read from ACTUAL envelope deaths.
  // "Lived through" it is a local-presence claim same as anything below —
  // capped by departureYear too, even though this block runs textually
  // before the Marriage section that narrates the departure itself
  // (departureYear is computed earlier precisely so this can read it).
  const localEnd = departureYear ?? p.death.year;
  let mentionedPlagues = 0;
  for (const pl of PLAGUES) {
    if (pl[1] < p.birth || pl[0] > localEnd) continue;
    const ageAt = Math.max(0, pl[0] - p.birth);
    if (localEnd >= pl[0] && !(p.death.cause === "plague" && p.death.year <= pl[1] + 0)) {
      if (localEnd < pl[0]) continue;
      if (ageAt < 3) continue;
      // did this plague actually take kin? — read the record, don't invent
      const kin: string[] = [];
      if (father && father.death.cause === "plague" && father.death.year >= pl[0] && father.death.year <= pl[1]) kin.push(ca ? "el seu pare" : pos + " father");
      if (mother && mother.death.cause === "plague" && mother.death.year >= pl[0] && mother.death.year <= pl[1])
        kin.push(ca ? "la seva mare" : pos + " mother");
      const sibsLost = siblings.filter((s) => s.death.cause === "plague" && s.death.year >= pl[0] && s.death.year <= pl[1]).length;
      if (sibsLost) kin.push(ca ? (sibsLost === 1 ? "un germà" : sibsLost + " germans") : sibsLost === 1 ? "a sibling" : sibsLost + " siblings");
      const kidsLost = children.filter((c) => c.death.cause === "plague" && c.death.year >= pl[0] && c.death.year <= pl[1]).length;
      if (kidsLost) kin.push(ca ? (kidsLost === 1 ? "un fill" : kidsLost + " fills") : kidsLost === 1 ? "a child" : kidsLost + " children");
      const spouseLost = !!(spouse && spouse.death.cause === "plague" && spouse.death.year >= pl[0] && spouse.death.year <= pl[1]);
      if (p.death.year <= pl[1] && p.death.cause === "plague") continue; // their own death entry covers it
      if (kin.length || spouseLost || mentionedPlagues === 0 || rng.chance(0.3)) {
        let t = ca ? `Va viure ${pl[3].ca}.` : `Lived through ${pl[3].en}.`;
        if (kin.length || spouseLost) {
          const all = kin.slice(); // spouse handled by its own entry below
          if (all.length)
            t += ca
              ? ` El registre d'enterraments mostra la casa desfeta: en van morir ${all.join(", ")}.`
              : ` The record of burials shows the household broken: ${all.join(", ")} died of it.`;
        } else if (pl[2] >= 10) {
          t += ca
            ? ` La casa se'n va lliurar, tot i que va morir mig poble i els camps van quedar sense collir per manca de mans.`
            : ` The household was spared, though half the parish died and fields lay unharvested for want of hands.`;
        }
        ev(pl[0] + (ageAt === 0 ? 1 : 0), t, "plague");
        mentionedPlagues++;
        // Bargaining with THIS manor's steward, or taking up a dead
        // neighbour's land, is its own local-presence claim, dated a year
        // past the plague itself — check localEnd again rather than
        // trusting the outer loop's start-year check to still cover it.
        if (pl[2] >= 10 && ageAt >= 10 && wealth <= 2 && pl[1] + 1 <= localEnd && rng.chance(0.6)) {
          ev(
            pl[1] + 1,
            p.cls === "serf"
              ? ca
                ? `En el camp buidat després de la mortaldat, la mà d'obra es va tornar cara. ${p.name} va negociar amb el batlle de ${fief.manor} unes prestacions més lleugeres — o simplement les va deixar de fer, com gosaven ja arreu els pagesos.`
                : `In the emptied countryside after the mortality, labour grew dear. ${p.name} bargained with the steward of ${fief.manor} for lighter services — or simply withheld them, as tenants everywhere now dared to do.`
              : ca
                ? `Amb tantes terres buides després de la mortaldat, ${p.name} va prendre les terres de veïns morts a preu baix, i la família va menjar millor que abans.`
                : `With so many holdings vacant after the mortality, ${p.name} took on the land of dead neighbours at low rent, and the family ate better than before.`,
            "fortune",
            cite("court"),
          );
        }
      }
    }
  }

  // Famine — same local-presence claim as the plague passages above.
  if (famineAt(Math.max(p.birth, region.famine[0]), region) && localEnd >= region.famine[0] && p.birth <= region.famine[1] && wealth <= 2) {
    const y = Math.max(p.birth + 1, region.famine[0]);
    if (y <= localEnd && y - p.birth >= 2)
      ev(
        y,
        ca
          ? `Les pluges van arruïnar les collites i va arribar la fam — ${region.famineName.ca}. La família va vendre el que va poder, es va menjar el blat de sement, i va sobreviure a base de pa captat i arrels collides.`
          : `The rains ruined the harvests and famine came — ${region.famineName.en}. The family sold what it could, ate the seed corn, and survived on begged bread and gathered roots.`,
        "famine",
        cite("account"),
      );
  }

  // § wardship: a full orphan below working age needed a guardian — for the
  // landed classes the lord/manor court typically took the wardship (and
  // its profits) until majority; for humbler families, kin took the child
  // in, a duty the court roll sometimes records alongside who now answered
  // for the household's dues.
  if (father && mother) {
    const orphanedAt = Math.max(father.death.year, mother.death.year);
    const orphanedAge = orphanedAt - p.birth;
    if (orphanedAge >= 0 && orphanedAge < 14 && orphanedAt < p.death.year) {
      // § canonical identity: an immigrant orphaned as a child was orphaned
      // in HER OWN origin village — years before her marriage ever brought
      // her onto THIS (destination) register — so the wardship's lord and
      // court citation must come from the origin village's own hierarchy
      // (fatherEnvForOcc, already resolved above), never this envelope's,
      // or a bride pulled in from elsewhere would be narrated as a ward of
      // a lord she in fact never lived under as a child.
      const wardEnv = fatherEnvForOcc ?? env;
      const wardFief = manorOf(wardEnv.worldSeed, wardEnv.regionKey, wardEnv.villageIdx, locale);
      // § nobility: the granting lord is the head of that manor's line in
      // the orphaning year, not the fief card's mid-register anchor.
      const wardLord = lordOfManorAt(wardEnv.worldSeed, wardEnv.regionKey, wardEnv.villageIdx, orphanedAt).name;
      const wardCite = citeDocument(
        "court",
        { jurisdiction: parishOf(wardEnv.worldSeed, wardEnv.regionKey, wardEnv.villageIdx, locale), fief: wardFief, place: wardEnv.place[locale] },
        locale,
      );
      ev(
        orphanedAt,
        wealth >= 4
          ? ca
            ? `Orfe de pare i mare, la tutela de la seva persona i de la seva terra va ser atorgada per ${wardLord} a un parent, que en cobraria els fruits fins que arribés a la majoria d'edat.`
            : `Orphaned of both parents, wardship of ${pos} person and land was granted by ${wardLord} to a kinsman, who would take the profits of the holding until ${obj} came of age.`
          : ca
            ? `Orfe de pare i mare, el van acollir uns parents, com va quedar constatat quan la cort del senyoriu va confirmar qui responia ara pels drets de la casa.`
            : `Orphaned of both parents, ${p.name} was taken in by kin, as the manor court noted when it confirmed who now answered for the household's dues.`,
        "grief",
        wardCite,
        // The granting lord links to HIS OWN page under his manor's line —
        // the origin village's for an immigrant ward, same as the citation.
        wealth >= 4
          ? [
              {
                id: -1,
                name: wardLord,
                addr: addrOnly(wardEnv),
                route: "lord",
                routeIdx: tenureIndexAt(manorLineOf(wardEnv.worldSeed, wardEnv.regionKey, wardEnv.villageIdx).heads, orphanedAt),
              },
            ]
          : undefined,
      );
    }
  }

  // § stepfamily: a parent's later remarriage brought a stepparent into a
  // still-young child's household — read from the same union history that
  // drives remarriage (village.ts), so it only ever fires when it really
  // happened. Native-born only (siblings/natalIdx aren't resolved for an
  // immigrant's cross-village family — see the natalIdx computation above).
  function firstLaterUnion(parent: Person | null): { spouse: Person; year: number } | null {
    if (!parent?.unions || natalIdx == null) return null;
    const natalYear = env.couples[natalIdx].year;
    const later = parent.unions
      .map((ci) => env.couples[ci])
      .filter((c) => c.year > natalYear)
      .sort((a, b) => a.year - b.year)[0];
    if (!later) return null;
    return { spouse: env.persons[parent.id === later.husband ? later.wife : later.husband], year: later.year };
  }
  const stepMotherUnion = firstLaterUnion(father); // father remarried -> new wife is a stepmother
  const stepFatherUnion = firstLaterUnion(mother); // mother remarried -> new husband is a stepfather
  if (stepFatherUnion) {
    const ageAt = stepFatherUnion.year - p.birth;
    if (ageAt >= 0 && ageAt <= 16 && stepFatherUnion.year < p.death.year) {
      const s = stepFatherUnion.spouse;
      ev(
        stepFatherUnion.year,
        ca
          ? `La seva mare es va tornar a casar, amb ${s.name} ${s.surname}, que va entrar a la casa com a padrastre.`
          : `${pos.charAt(0).toUpperCase() + pos.slice(1)} mother married again, to ${s.name} ${s.surname}, who came into the house as ${pos} stepfather.`,
        "life",
        undefined,
        [nameRef(s, selfAddr)],
      );
    }
  }
  if (stepMotherUnion) {
    const ageAt = stepMotherUnion.year - p.birth;
    if (ageAt >= 0 && ageAt <= 16 && stepMotherUnion.year < p.death.year) {
      const s = stepMotherUnion.spouse;
      ev(
        stepMotherUnion.year,
        ca
          ? `El seu pare es va tornar a casar, amb ${s.name} ${s.surname}, que va entrar a la casa com a madrastra.`
          : `${pos.charAt(0).toUpperCase() + pos.slice(1)} father married again, to ${s.name} ${s.surname}, who came into the house as ${pos} stepmother.`,
        "life",
        undefined,
        [nameRef(s, selfAddr)],
      );
    }
  }

  // Occupation (biographyOccupation.ts) — text stays consistent with the
  // mechanical riskTrade tag rolled at Tier 1 (village.ts): a
  // hazardous/maritime/military person is preferentially narrated into a
  // trade that actually carries that hazard, never the reverse. Reads the
  // same `rng` instance decodePerson already holds, at the same point in
  // its draw sequence as the inline version this replaced.
  const decodedOcc = decodeOccupation(p, locale, rng, everMarried, widowedUnions, own, spouseOf);
  if (decodedOcc) {
    occupation = decodedOcc.text;
    if (decodedOcc.literate) literate = true;
    ev(decodedOcc.year, `${p.name} ${decodedOcc.text}.`, "life");
  }
  // § the celibate estate: entry into religion (villageMobility.ts's
  // rollVocation). Now reachable from any house and by either sex, so the
  // text can no longer assume a tonsured chantry priest: a daughter of a
  // dowried house went to a convent, and the entry gift her family found for
  // it — a fraction of the marriage dowry they were spared — is the whole
  // reason the cloister was where such a daughter ended up.
  if (p.inOrders) {
    literate = true;
    const dowried = p.sex === "F" && CLASS_INFO[p.cls].wealth >= 3;
    ev(
      p.birth + 14,
      p.sex === "M"
        ? ca
          ? `Va ser destinat a l'Església: va aprendre llatí del capellà de la parròquia, va rebre la tonsura, i amb el temps va prendre els ordes. Va servir com a capellà de capellania, cantant misses pels morts dels anys de pesta.`
          : `Was marked out for the Church: learned Latin from the parish priest, was tonsured, and in time took orders. Served as a chantry priest, singing masses for the dead of the plague years.`
        : dowried
          ? ca
            ? `Va ser destinada al claustre en lloc del matrimoni: la casa va pagar la dot d'entrada — molt menor que la que li hauria calgut trobar per casar-la — i va prendre el vel, amb el saltiri i els comptes de la casa religiosa al seu càrrec.`
            : `Was given to the cloister rather than to a husband: the house paid her entry gift — a fraction of the marriage dowry it was spared — and she took the veil, keeping her psalter and, in time, the convent's accounts.`
          : ca
            ? `Va entrar en religió: va prendre el vel en una casa petita de la comarca, on va aprendre les lletres que a casa no hauria après mai.`
            : `Entered religion: took the veil in a small house within the county, and learned there the letters she would never have learned at home.`,
      "life",
    );
  }

  // § service: adolescent years in another household, rolled at Tier 1.
  // § service placement: the master is a real householder on this same
  // register (service.ts), so the entry names him and links to his record
  // rather than leaving the reader with an anonymous "another household".
  // Only where the village had nobody suitable standing that year does it
  // fall back to the manorial familia, which is exactly what `serviceMaster`
  // being unset means.
  if (p.service && p.death.year > p.service.from) {
    const years = Math.min(p.service.to, p.death.year) - p.service.from;
    const span = years > 1 ? (ca ? `${years} anys` : `${years} years`) : ca ? "un any" : "a year";
    const master = p.serviceMaster != null ? env.persons[p.serviceMaster] : null;
    const house = master
      ? ca
        ? `a casa de ${master.name} ${master.surname}`
        : `in the household of ${master.name} ${master.surname}`
      : ca
        ? "a la casa senyorial"
        : "in the manor household";
    ev(
      p.service.from,
      ca
        ? `Va entrar a servir ${house}, com era costum per als fills de cases humils, i hi va passar ${span} guanyant-se el llit, la taula i la soldada${p.unions?.length ? ", fins que va tenir casa pròpia" : ""}.`
        : `Went into service ${house}, as was the custom for children of humble houses, and spent ${span} there earning bed, board, and a small wage${p.unions?.length ? `, until there was a household of ${p.sex === "M" ? "his" : "her"} own to leave for` : ""}.`,
      "life",
      cite("account"),
      master ? [nameRef(master, selfAddr)] : undefined,
    );
  }

  // § mobility: the person left their natal class on coming of age — either
  // up (serfToFree/freeToArtisan/artisanToMerchant, village.ts rollMobility)
  // or, for a non-heir son under impartible custom, down (§ downward
  // mobility, rollDownwardMobility). Direction is read off the wealth
  // grades themselves, not stored separately on the Person.
  if (p.clsOrigin && p.death.age >= 16 && (departureYear == null || departureYear - p.birth >= 16)) {
    const y = p.birth + 16;
    const rose = CLASS_INFO[p.cls].wealth > CLASS_INFO[p.clsOrigin].wealth;
    let t: string;
    if (rose) {
      if (p.clsOrigin === "serf")
        t = ca
          ? `Va sortir de la servitud: en el camp despoblat va aconseguir una tinença lliure, i el registre {{el/la}} comença a anomenar pagès{{/a}} lliure.`
          : `Rose out of servitude: in the emptied countryside ${p.sex === "M" ? "he" : "she"} secured a free tenancy, and the rolls begin to style ${obj} a free tenant.`;
      else if (p.clsOrigin === "freePeasant")
        t = ca
          ? `Va deixar la terra per un ofici: aprenentatge a un taller, i amb els anys un lloc propi entre l'artesanat del poble.`
          : `Left the land for a trade: an apprenticeship in a workshop, and in time a place among the village artisans.`;
      else
        t = ca
          ? `Va arriscar els estalvis del taller en el comerç, i li va anar bé: la casa va passar a viure dels tractes i no de les mans.`
          : `Ventured the workshop's savings in trade, and prospered: the household came to live by dealing rather than by its hands.`;
    } else if (p.clsOrigin === "merchant")
      t = ca
        ? `Sense taller ni crèdit que heretar, només li va quedar l'ofici de les mans: el registre {{el/la}} comença a anomenar simplement artesà{{/na}}, no mercader.`
        : `With no shop or its credit to inherit, ${p.sex === "M" ? "he" : "she"} kept only the trade of the hands: the rolls begin to style ${obj} a plain artisan, not a merchant.`;
    else
      t = ca
        ? `Sense taller propi que heretar, va tornar a treballar la terra: el registre {{el/la}} comença a anomenar pagès{{/a}}, no artesà{{/na}}.`
        : `With no workshop of his own to inherit, ${p.sex === "M" ? "he" : "she"} went back to working the land: the rolls begin to style ${obj} a peasant, not an artisan.`;
    ev(y, t, rose ? "fortune" : "hardship", cite("court"));
  }

  // § manorial accounts: a periodic entry in the reeve's roll — the rent
  // and boon-works owed for the holding, sometimes in arrears after a bad
  // harvest — for the tenant classes who actually rendered such dues in
  // this model (artisans/merchants/gentry/clergy held by other tenures).
  // Presupposes she's still the one holding it, so capped by departure too.
  const acctAge = departureYear != null ? Math.min(p.death.age, departureYear - p.birth) : p.death.age;
  if ((p.cls === "serf" || p.cls === "freePeasant") && acctAge >= 20 && rng.chance(0.3)) {
    const y = p.birth + rng.int(20, Math.min(50, acctAge));
    const badYear = famineAt(y, region) || !!warAt(y, region);
    ev(
      y,
      badYear
        ? ca
          ? `El rotlle del batlle {{el/la}} anota en deute aquell any — la renda i les prestacions no pagades, ajornades contra la collita següent.`
          : `The reeve's roll notes ${obj} in arrears that year — rent and boon-works owed and not yet paid, carried forward against next year's harvest.`
        : ca
          ? `El rotlle del batlle registra ${p.name} rendint la renda i les prestacions acostumades de la tinença, en diners i en espècie, com cada any.`
          : `The reeve's roll records ${p.name} rendering the customary rent and boon-works for the holding, in coin and kind, as every year.`,
      badYear ? "hardship" : "life",
      cite("account"),
    );
  }

  // § inheritance: succession to the father's holding, from succession.ts —
  // the same rule the temporal resolver uses for household headship.
  if (p.father >= 0 && inheritedFromFather(env, id)) {
    const father = env.persons[p.father];
    if (father.death.year > p.birth + 12 && father.death.year <= p.death.year) {
      ev(
        father.death.year,
        ca
          ? `A la mort del seu pare va entrar a la tinença com a hereu{{/va}}, pagant al senyor el lluïsme acostumat — la millor bèstia de la casa.`
          : `At ${pos} father's death entered the holding as heir, paying the lord the customary relief — the best beast of the house.`,
        "fortune",
        cite("court"),
      );
    }
  } else if (p.father >= 0 && region.inheritance === "partible" && p.sex === "M" && !p.emigrated && !p.inOrders) {
    // § regional inheritance customs: under partible custom (France,
    // Tuscany — regions.ts) land was divided among the sons instead of
    // passing whole to one — a surviving, non-departing son who wasn't the
    // customary household heir still took a real share, narrated distinctly
    // from both the sole-heir text above and plain silence.
    const father = env.persons[p.father];
    if (father.death.year > p.birth + 12 && father.death.year <= p.death.year) {
      ev(
        father.death.year,
        ca
          ? `A la mort del seu pare va rebre la seva part de la terra, partida entre els fills com era costum aquí, no passada sencera a un de sol.`
          : `At ${pos} father's death received ${pos} share of the land, divided among the sons as was the custom here, not passed whole to one.`,
        "fortune",
        cite("court"),
      );
    }
  }

  // Marriage — real spouses from the matching, first union and any remarriage
  unionCouples.forEach((c, i) => {
    const s = spouseOf(c);
    const sName = s.name + " " + s.surname;
    // § the season: keyed on the couple, so this is the same day her own
    // record and his own record both name.
    const wedDate = marriageDate(p.unions![i], c.year);
    if (i === 0) {
      let extra = "";
      // Merchet was HER FATHER'S payment for licence to marry her off — so
      // it only ever happened if he was actually alive to make it, not just
      // recorded somewhere earlier in her life (same-shape fix as the
      // maritalGate occupation entries above: a fact tied to a real event
      // must be checked against that event's own year, not "ever true").
      // § nobility: the lord named is the one actually holding the manor in
      // the marriage year — a court roll dated that year names that head of
      // the line, not the fief card's mid-register anchor.
      const merchetLord = lordOfManorAt(env.worldSeed, env.regionKey, env.villageIdx, c.year).name;
      let merchetPaid = false;
      if (p.cls === "serf" && p.sex === "F" && father && father.death.year > c.year) {
        extra = ca ? ` El seu pare va pagar a ${merchetLord} el dret per casar-la.` : ` Her father paid merchet to ${merchetLord} for licence to marry.`;
        merchetPaid = true;
      }
      if (s.incomer && p.sex === "M") {
        extra = s.origin
          ? ca
            ? ` Ella venia de ${placeNameOf(env.worldSeed, s.origin, locale)}, amb un dot de roba de lli i una vaca.`
            : ` She came from ${placeNameOf(env.worldSeed, s.origin, locale)}, with a dowry of linen and a cow.`
          : ca
            ? ` Ella venia de la parròquia veïna, amb un dot de roba de lli i una vaca.`
            : ` She came from the next parish, with a dowry of linen and a cow.`;
      }
      const older = p.sex === "F" && s.birth < p.birth - 3;
      // § bridal pregnancy (village.ts's genChildren): a first child baptised
      // inside the wedding year is the register's own signature of a
      // conception that preceded the wedding — and the commonest irregularity
      // it records, several times commoner than illegitimacy proper. Read off
      // the dates rather than stored as a flag: the couple's own first child
      // and the couple's own year are already there to compare (§ pure
      // decode). Not narrated as scandal, because it largely wasn't one where
      // a promise of marriage was itself binding.
      const firstBorn = c.children.length ? env.persons[c.children[0]] : null;
      if (firstBorn && firstBorn.birth === c.year && !firstBorn.illegitimate)
        extra += ca
          ? ` El primer fill va ser batejat abans no s'acabés l'any del casament, com passava sovint quan la promesa precedia l'església.`
          : ` Their first child was baptised before the year of the wedding was out, as often happened where the promise came before the church.`;
      // § consanguinity: flagged by village.ts's marry(), not blocked — a
      // first-cousin match required a bishop's dispensation after Lateran IV
      // (1215), which is worth its own sentence rather than passing silently.
      if (c.consanguineous)
        extra += ca
          ? ` Eren cosins germans; calgué una dispensa del bisbe per casar-los.`
          : ` They were first cousins; a bishop's dispensation was needed for the match.`;
      // § affinity: this is P's own FIRST marriage (i===0), but it can still
      // be S's REMARRIAGE — meaning P himself is the one who turns out to be
      // a sibling of S's own late first spouse (village.ts's isAffinal put
      // the "new" party in that role either way). Symmetric to the
      // remarriage-branch wording further down, just from the other side.
      if (c.affinal) {
        const sibWord = p.sex === "M" ? (ca ? "germà" : "brother") : ca ? "germana" : "sister";
        const lateWord = s.sex === "F" ? (ca ? "del seu difunt marit" : "of her late husband") : ca ? "de la seva difunta esposa" : "of his late wife";
        extra += ca
          ? ` {{Ell/Ella}} era ${sibWord} ${lateWord}; calgué una dispensa per aquest parentiu.`
          : ` {{He/She}} was a ${sibWord} ${lateWord}; a dispensation was needed for the match.`;
      }
      // § nobility links: the merchet lord is clickable — to his own lord
      // page, not to any register record (a lord has none).
      const mRefs = [nameRef(s, selfAddr)];
      if (merchetPaid)
        mRefs.push({
          id: -1,
          name: merchetLord,
          addr: selfAddr,
          route: "lord",
          routeIdx: tenureIndexAt(manorLineOf(env.worldSeed, env.regionKey, env.villageIdx).heads, c.year),
        });
      // § nobility crosslink: a modest share of gentry matches to a NATIVE
      // spouse (not an incomer decoded elsewhere) are kin of the manor's own
      // lord line — a real, addressable NobleLine (nobility.ts), not an
      // invented relation, so the reference links to that lord's own page
      // exactly like the merchet lord above.
      if (p.cls === "gentry" && !s.incomer && rng.chance(0.12)) {
        const kinLord = lordOfManorAt(env.worldSeed, env.regionKey, env.villageIdx, c.year);
        extra += ca
          ? ` Era parent llunyà de ${kinLord.name}, que aleshores tenia la senyoria.`
          : ` A distant kinsman of ${kinLord.name}, who then held the manor.`;
        mRefs.push({
          id: -1,
          name: kinLord.name,
          addr: selfAddr,
          route: "lord",
          routeIdx: tenureIndexAt(manorLineOf(env.worldSeed, env.regionKey, env.villageIdx).heads, c.year),
        });
      }
      ev(
        c.year,
        ca
          ? `Es va casar amb ${sName}${older ? ", un home uns quants anys més gran, com era habitual" : ""}.${extra}`
          : `Married ${sName}${older ? ", a man some years older, as was usual" : ""}.${extra}`,
        "marriage",
        undefined,
        mRefs,
        wedDate,
      );
    } else {
      // § remarriage
      const consangText = c.consanguineous
        ? ca
          ? ` Eren cosins germans; calgué una dispensa del bisbe per casar-los.`
          : ` They were first cousins; a bishop's dispensation was needed for the match.`
        : "";
      // § affinity: `s` is a sibling of p's own LATE spouse (village.ts's
      // isAffinal) — kin by marriage, not blood, but canon law still
      // required a dispensation for it, same as consanguinity above.
      const affinalText = c.affinal
        ? (() => {
            const sibWord = s.sex === "M" ? (ca ? "germà" : "brother") : ca ? "germana" : "sister";
            const lateWord = p.sex === "M" ? (ca ? "de la seva difunta esposa" : "of his late wife") : ca ? "del seu difunt marit" : "of her late husband";
            return ca
              ? ` ${sName} era ${sibWord} ${lateWord}; calgué una dispensa per aquest parentiu.`
              : ` ${sName} was a ${sibWord} ${lateWord}; a dispensation was needed for the match.`;
          })()
        : "";
      ev(
        c.year,
        ca
          ? `Es va tornar a casar, amb ${sName}: una casa no es podia portar sola, i el dol durava el que durava.${consangText}${affinalText}`
          : `Married again, to ${sName}: a household could not be run alone, and mourning lasted only as long as it could afford to.${consangText}${affinalText}`,
        "marriage",
        undefined,
        [nameRef(s, selfAddr)],
        wedDate,
      );
    }
  });
  if (!own && p.marriedOut) {
    const dest = p.emigrateTo;
    const destPlace = placeNameOf(env.worldSeed, dest, locale);
    const destText =
      destPlace && dest
        ? ca
          ? p.longDistance
            ? ` a ${destPlace}, a ${REGIONS[dest.regionKey].name.ca}`
            : ` a ${destPlace}`
          : p.longDistance
            ? ` to ${destPlace}, in ${REGIONS[dest.regionKey].name.en}`
            : ` to ${destPlace}`
        : "";
    if (destPerson && destUnion && destSpouse && destRecord) {
      // § canonical identity: the destination register really recorded her —
      // name the husband and the year, and let the Bio point at that record.
      ev(
        destUnion.year,
        ca
          ? `Es va casar fora de la parròquia${destText}, amb ${destSpouse.name} ${destSpouse.surname}; el registre d'aquell poble la recull des d'aleshores, i la resta de la seva vida hi queda escrita.`
          : `Married out of the parish${destText}, to ${destSpouse.name} ${destSpouse.surname}; that village's register carries her from then on, and the rest of her life is written there.`,
        "marriage",
        undefined,
        [nameRef(destSpouse, destRecord)],
      );
    } else {
      ev(
        departureYear!,
        ca
          ? `Es va casar fora de la parròquia${destText}; el seu nom surt d'aquest registre cap a un altre, i la resta de la seva vida hi queda escrita.`
          : `Married out of the parish${destText}; her name leaves this register for another, and the rest of her life is written there.`,
        "marriage",
        undefined,
        // § the far end: there is no record of her at the other end — the
        // rank rule forbids inventing one — but the PLACE is real, and it
        // can name her among its incomers. So the destination is a link
        // rather than a full stop.
        destPlace && dest ? [{ id: -1, name: destPlace, addr: dest, route: "village" }] : undefined,
      );
    }
  } else if (!own && p.cityService) {
    // § the marriage squeeze: she left for service in a town. Narrated
    // distinctly from "married out", because that is the whole point of the
    // flag: the parish register recorded a daughter leaving, not a wedding it
    // never saw. If the destination register did go on to record a marriage,
    // say so and link it — but as that village's fact, arrived at afterward.
    const dest = p.emigrateTo;
    const destPlace = placeNameOf(env.worldSeed, dest, locale);
    const destText =
      destPlace && dest
        ? ca
          ? p.longDistance
            ? ` a ${destPlace}, a ${REGIONS[dest.regionKey].name.ca}`
            : ` a ${destPlace}`
          : p.longDistance
            ? ` in ${destPlace}, in ${REGIONS[dest.regionKey].name.en}`
            : ` in ${destPlace}`
        : "";
    ev(
      departureYear!,
      ca
        ? `Va entrar a servir en una casa${destText}, com tantes filles que no tenien dot; els anys de soldada eren, de fet, com es reunia el dot.`
        : `Went into service in a household${destText}, as so many daughters did who had no dowry; the wage years were how a dowry was got together.`,
      "life",
    );
    if (destPerson && destUnion && destSpouse && destRecord) {
      ev(
        destUnion.year,
        ca
          ? `Es va casar allà, amb ${destSpouse.name} ${destSpouse.surname}; el registre d'aquell poble la recull des d'aleshores, i la resta de la seva vida hi queda escrita.`
          : `Married there, to ${destSpouse.name} ${destSpouse.surname}; that village's register carries her from then on, and the rest of her life is written there.`,
        "marriage",
        undefined,
        [nameRef(destSpouse, destRecord)],
      );
    }
  } else if (!own && p.sex === "M" && p.emigrated) {
    // § male out-migration: a non-heir who left, narrated distinctly from a
    // woman's "married out" — he may or may not have gone to marry.
    const dest = p.emigrateTo;
    const destPlace = placeNameOf(env.worldSeed, dest, locale);
    const destText =
      destPlace && dest
        ? ca
          ? p.longDistance
            ? ` cap a ${destPlace}, a ${REGIONS[dest.regionKey].name.ca}`
            : ` cap a ${destPlace}`
          : p.longDistance
            ? ` for ${destPlace}, in ${REGIONS[dest.regionKey].name.en}`
            : ` for ${destPlace}`
        : "";
    if (destPerson && destUnion && destSpouse && destRecord) {
      // § canonical identity: the destination register really recorded him.
      ev(
        destUnion.year,
        ca
          ? `Va deixar la parròquia${destText} i allà es va casar amb ${destSpouse.name} ${destSpouse.surname}; el registre d'aquell poble el recull des d'aleshores, i la resta de la seva vida hi queda escrita.`
          : `Left the parish${destText} and there married ${destSpouse.name} ${destSpouse.surname}; that village's register carries him from then on, and the rest of his life is written there.`,
        "marriage",
        undefined,
        [nameRef(destSpouse, destRecord)],
      );
    } else {
      const heirApparent = region.inheritance === "partible" || isFirstBornSon(env, id);
      const reasons = ca
        ? [
            `Sense terra pròpia que esperar, va marxar${destText} a provar sort en un ofici.`,
            `Va entrar al seguici armat d'un senyor, i el registre no en sap dir més que va marxar.`,
            `Va anar a servir en una altra casa senyorial${destText}, no havent-hi tinença aquí per a un segon fill.`,
          ]
        : [
            `With no holding of his own to wait for, he left${destText} to try his luck at a trade.`,
            `Took service in a lord's armed retinue, and the register can say no more than that he left.`,
            `Went to serve in another manor's household${destText}, there being no tenement here for a second son.`,
          ];
      const leftText =
        heirApparent || p.death.age < region.marriageM[1] + 3
          ? ca
            ? `Va marxar de la parròquia${destText}; el seu nom surt d'aquest registre cap a un altre, i la resta de la seva vida hi queda escrita.`
            : `Left the parish${destText}; his name leaves this register for another, and the rest of his life is written there.`
          : rng.pick(reasons);
      // § the far end: same as the married-out case — the place he went to
      // is a real, browsable address even though he has no record in its
      // register. Gated on the chosen text ACTUALLY naming it: one of the
      // reasons above ("took service in a lord's armed retinue, and the
      // register can say no more than that he left") deliberately does not,
      // and a ref whose name is not a substring of its own event breaks the
      // one contract linkifyEventText has.
      const placeRef: EventRef[] | undefined =
        destPlace && dest && leftText.includes(destPlace) ? [{ id: -1, name: destPlace, addr: dest, route: "village" }] : undefined;
      ev(departureYear!, leftText, "life", undefined, placeRef);
    }
  } else if (!own && !p.marriedOut && !p.emigrated && p.death.age >= 30 && !p.inOrders && rng.chance(0.7)) {
    ev(
      p.birth + 30,
      ca
        ? `No es va casar mai — el registre {{el/la}} mostra a la mateixa casa any rere any, ${p.sex === "F" ? "filant i portant la casa dels seus parents" : "treballant la terra familiar"}.`
        : `Never married — the register shows ${obj} in the same household year after year, ${p.sex === "F" ? "spinning and keeping house for kin" : "labouring on the family land"}.`,
      "life",
    );
  }

  // Children — real, from the envelope
  for (const c of children) {
    const cp = plagueAt(c.birth, env.regionKey);
    if (c.death.age === 0) {
      ev(
        c.birth,
        ca
          ? `Va néixer ${c.sex === "M" ? "un fill" : "una filla"}, ${c.name}, i va morir abans de complir l'any, ${cp && c.death.cause === "plague" ? "de la pesta que aleshores feia estralls" : "com un de cada quatre nadons"}. Enterrat{{/da}} al cementiri, sense làpida.`
          : `A ${c.sex === "M" ? "son" : "daughter"}, ${c.name}, was born — and died within the year, ${cp && c.death.cause === "plague" ? "of the pestilence then raging" : "as one in four infants did"}. Buried in the churchyard, unmarked.`,
        "grief",
        undefined,
        [nameRef(c, selfAddr, false)],
        birthDateOfPerson(c),
      );
    } else {
      ev(
        c.birth,
        ca
          ? `Va néixer ${c.sex === "M" ? "un fill" : "una filla"}, ${c.name}${c.death.age >= 16 ? ", que va viure" : ""}.`
          : `A ${c.sex === "M" ? "son" : "daughter"}, ${c.name}, was born${c.death.age >= 16 ? ", and lived" : ""}.`,
        "child",
        undefined,
        [nameRef(c, selfAddr, false)],
        birthDateOfPerson(c),
      );
      if (c.death.age < 16) {
        const cause =
          c.death.cause === "plague"
            ? ca
              ? "de la pesta"
              : "of the pestilence"
            : ca
              ? "de " + rng.pick(CHILD_DEATH_CAUSE.ca)
              : "of " + rng.pick(CHILD_DEATH_CAUSE.en);
        ev(
          c.death.year,
          ca
            ? `${c.name} va morir als ${c.death.age} anys, ${cause}. Els pares van pagar ciris a l'altar de la Mare de Déu.`
            : `${c.name} died at ${c.death.age}, ${cause}. The parents paid for candles at the altar of the Virgin.`,
          "grief",
          undefined,
          [nameRef(c, selfAddr, false)],
        );
      }
    }
  }

  // Spouse death & widowhood — real, once per union that ended in bereavement
  for (const c of unionCouples) {
    const s = spouseOf(c);
    if (s.death.year >= p.death.year) continue;
    const atHome = children.filter((k) => k.death.year > s.death.year && s.death.year - k.birth < 15 && k.birth < s.death.year).length;
    const causeTxt = ca
      ? s.death.cause === "plague"
        ? " de la pesta"
        : s.death.cause === "childbirth"
          ? " de part"
          : ""
      : s.death.cause === "plague"
        ? " of the pestilence"
        : s.death.cause === "childbirth"
          ? " in childbed"
          : "";
    ev(
      s.death.year,
      ca
        ? `${s.name} ${s.surname} va morir${causeTxt}, deixant ${p.name} {{vidu/vídua}}${atHome ? ` amb ${atHome} fills encara a casa` : ""}.`
        : `${s.name} ${s.surname} died${causeTxt}, leaving ${p.name} ${p.sex === "F" ? "a widow" : "a widower"}${atHome ? ` with ${atHome} children still at home` : ""}.`,
      "grief",
      undefined,
      [nameRef(s, selfAddr)],
    );
  }

  // Revolt — "swept the district" assumes she was still in the district;
  // capped by departureYear like every other localized block below.
  if (
    region.revolt &&
    region.revolt.year >= p.birth + 15 &&
    region.revolt.year <= Math.min(departureYear ?? p.death.year, p.birth + 55) &&
    wealth <= 3 &&
    rng.chance(0.35)
  ) {
    const joined = rng.chance(0.55);
    const rname = region.revolt.name[locale],
      rdesc = region.revolt.desc[locale];
    ev(
      region.revolt.year,
      joined
        ? ca
          ? `Va esclatar ${rname} — ${rdesc}. ${p.name} va ser entre els qui es van revoltar, i després en va callar la resta de la seva vida, perquè les represàlies van ser dures.`
          : `${rname} broke out — ${rdesc}. ${p.name} was among those who rose, and afterwards kept quiet about it for the rest of ${pos} life, for the reprisals were savage.`
        : ca
          ? `${rname} va sacsejar el districte — ${rdesc}. ${p.name} va ajupir el cap i va tancar la porta.`
          : `${rname} swept the district — ${rdesc}. ${p.name} kept ${pos} head down and the door barred.`,
      "revolt",
      cite("chron"),
    );
  }

  // War touching the village — same departure cap: soldiers "coming near"
  // is a local fact, false once she's no longer local.
  for (const [a, b] of region.warYears) {
    const localEnd = departureYear ?? p.death.year;
    if (b < p.birth + 6 || a > localEnd) continue;
    if (rng.chance(0.35) && wealth <= 3) {
      const y = Math.max(a, p.birth + 6) + rng.int(0, Math.max(0, Math.min(b, localEnd) - Math.max(a, p.birth + 6)));
      const wn = warAt(y, region, locale) || (ca ? "les guerres" : "the wars");
      const action = ca ? rng.pick(WAR_ACTION.ca) : rng.pick(WAR_ACTION.en);
      ev(
        y,
        ca
          ? `${wn.charAt(0).toUpperCase() + wn.slice(1)} va arribar a prop: uns soldats ${action}.`
          : `${wn.charAt(0).toUpperCase() + wn.slice(1)} came near: soldiers ${action}.`,
        "war",
        cite("account"),
      );
      break;
    }
  }

  // Personal texture events
  const earliestChildBirth = children.length ? Math.min(...children.map((c) => c.birth)) : null;
  function texture(pool: readonly [string, number, string | null][], lo: number, hi: number, n: number) {
    // § departure: a departed person's life here is over from that year on,
    // same as p.death.age already bounds a life that's over from death on —
    // capping `hi` covers every texture pool (CHILD/YOUTH/ADULT/OLD_EVENTS,
    // including the generic will pool entry) in one place.
    const effHi = departureYear != null ? Math.min(hi, departureYear - p.birth) : hi;
    const span = Math.min(effHi, p.death.age) - lo;
    if (span <= 0) return;
    let count = 0;
    for (const [tmpl, wgt, flag] of pool) {
      if (count >= n) break;
      if (flag === "englandM" && !(env.regionKey === "england" && p.sex === "M")) continue;
      // § texture marital gate: an entry that presupposes children of her
      // own (e.g. "raised them with {pos} own") must only ever be eligible
      // for someone who actually has any — otherwise it fires for a
      // childless adult.
      //
      // § the celibate estate: `inOrders` is now checked here in its own
      // right, rather than relied on second-hand. It used to be enough that
      // someone in orders could have no union and therefore no children; that
      // stopped being true when § illegitimacy began letting a man in orders
      // FATHER a natural child (village.ts — a priest's children are among
      // the best-attested illegitimacies there are). He has children now, but
      // he has no household to have raised them in, which is what these
      // entries describe.
      if (flag === "hasChildren" && (earliestChildBirth == null || p.inOrders)) continue;
      // § named wills: when a real heir is on record, the dedicated
      // named-heir event (below, after the OLD_EVENTS pass) replaces this
      // generic pool line rather than firing alongside it.
      if (flag === "will" && heir) continue;
      if (!rng.chance(wgt * 0.16)) continue;
      count++;
      // Never date it earlier than the year it's actually true: she has to
      // have a child ("her own") before she can raise one alongside it.
      const y = Math.max(p.birth + lo + rng.int(0, span), flag === "hasChildren" && earliestChildBirth != null ? earliestChildBirth : -Infinity);
      // § register blackout: only the parish-register-sourced entries
      // (the default, unflagged case) can go missing this way — a manor
      // court roll or a will has its own separate keeper, unaffected by
      // whatever befell the parish clerk.
      if (flag !== "court" && flag !== "will" && registerBlackoutAt(env.worldSeed, env.regionKey, env.villageIdx, y, region)) {
        blackoutYears.add(y);
        continue;
      }
      const text = tmpl
        .replace("{pos}", pos)
        .replace("{obj}", obj)
        .replace("{land}", region.landUnit[locale])
        .replace("{courtcause}", rng.pick(COURT_CAUSES[locale]));
      let src = cite("reg");
      if (flag === "court") src = cite("court");
      if (flag === "will") src = cite("will");
      if (flag === "literate") literate = true;
      ev(y, text, "life", src);
    }
  }
  texture(CHILD_EVENTS[locale], 5, 11, 2);
  texture(YOUTH_EVENTS[locale], 12, 20, 2);
  texture(ADULT_EVENTS[locale], 21, 58, 3);
  if (p.sex === "F" && own) texture(FEMALE_EVENTS[locale], 20, 45, 1);
  texture(OLD_EVENTS[locale], 58, 90, 2);

  // § register blackout: name the crisis once, rather than leaving the
  // texture-entry suppression above as an unexplained gap.
  if (blackoutYears.size) {
    const year = Math.min(...blackoutYears);
    const plague = plagueAt(year, env.regionKey);
    const crisis = plague ? plague[3][locale] : warAt(year, region, locale) || (ca ? "aquells anys" : "those years");
    ev(
      year,
      ca
        ? `El registre porta mal compte d'aquells anys — ${crisis} — quan la mà del clergue no era lliure, o no era viva, per escriure.`
        : `The register kept a poor account of those years — ${crisis} — when the clerk's hand was not free, or not living, to write.`,
      "life",
      cite("reg"),
    );
  }

  // § named wills: same age window and rough weight as the generic
  // OLD_EVENTS "will" pool line it replaces (texture()'s skip above keeps
  // the two from ever firing for the same person) — but this one, having a
  // real heir on record, names the holding's actual destination instead of
  // an anonymous bed and brass pot. Never fires once departed: a will
  // dictated to THIS parish clerk over a holding here presupposes she's
  // still the one holding it.
  if (heir && departureYear == null && p.death.age > 58 && rng.chance(1.2 * 0.16)) {
    const y = p.birth + 58 + rng.int(0, Math.min(90, p.death.age) - 58);
    const heirName = `${heir.name} ${heir.surname}`;
    ev(
      y,
      ca
        ? `Va dictar testament al clergue de la parròquia, deixant la casa a ${heirName}: diners per a la fàbrica de l'església, i pa per als pobres a l'enterrament.`
        : `Dictated a will to the parish clerk, naming ${heirName} to the holding: pence for the church fabric, and bread for the poor at the funeral.`,
      "life",
      cite("will"),
      [nameRef(heir, selfAddr)],
    );
  }

  // World events — run AFTER the texture passes above (not before): a
  // world event's text can read `literate`, and childhood literacy is only
  // ever set by the CHILD_EVENTS texture pass (ages 5-11). Reading it
  // beforehand always saw the initial `false`, so e.g. a child who WAS
  // taught letters at 5-11 could still get the illiterate-framed reaction
  // to the 1460s printing press. Narrative event ORDER in the finished
  // chronicle is unaffected either way (events.sort() below is by year,
  // not call order) — this only changes which value of `literate` this
  // block's own text-selection callback sees.
  for (const w of WORLD_EVENTS[locale]) {
    if (w[1] < p.birth + w[3] || w[0] > (departureYear ?? p.death.year)) continue;
    if (w[2] && !w[2].includes(env.regionKey)) continue;
    if (rng.chance(w[4])) ev(Math.max(w[0], p.birth + w[3]), w[7](p, locale, literate), w[5], SRC[locale][w[6] as DocumentKind]);
  }

  // § nobility: royal accessions and depositions, derived from ROYAL_LINES
  // (real history, not dice) — same shape and gating as the world events
  // above, appended after them so their rng draws stay in a fixed order.
  for (const w of royalWorldEvents(locale)) {
    if (w[1] < p.birth + w[3] || w[0] > (departureYear ?? p.death.year)) continue;
    if (w[2] && !w[2].includes(env.regionKey)) continue;
    if (rng.chance(w[4])) ev(Math.max(w[0], p.birth + w[3]), w[7](p, locale, literate), w[5], SRC[locale][w[6] as DocumentKind]);
  }

  // § nobility: a change of lord was parish news — homage sworn anew, the
  // relief taken, the court held under a new name. The successions are read
  // from the manor's own lord line, so every villager who mentions one
  // agrees on who died and who entered.
  {
    const line = manorLineOf(env.worldSeed, env.regionKey, env.villageIdx);
    const succ = line.heads
      .slice(1)
      .map((h, i) => ({ h, old: line.heads[i] }))
      .filter(({ h }) => h.acceded >= Math.max(p.birth + 10, 1292) && h.acceded <= (departureYear ?? p.death.year));
    if (succ.length && rng.chance(0.35)) {
      const { h, old } = rng.pick(succ);
      const hIdx = line.heads.indexOf(h);
      const died =
        old.cause === "war"
          ? ca
            ? " a la guerra del rei"
            : " in the king's wars"
          : old.cause === "plague"
            ? ca
              ? " en la mortaldat"
              : " in the pestilence"
            : "";
      ev(
        h.acceded,
        ca
          ? `El vell senyor ${old.name} morí${died}, i ${h.name} entrà en la senyoria; els tinents renovaren l'homenatge i la cort cobrà els seus drets.`
          : `The old lord ${old.name} died${died}, and ${h.name} entered the lordship; the tenants did homage anew and the court took its reliefs.`,
        "life",
        cite("court"),
        // Both lords link to their own pages under the manor's line.
        [
          { id: -1, name: old.name, addr: selfAddr, route: "lord", routeIdx: hIdx - 1 },
          { id: -1, name: h.name, addr: selfAddr, route: "lord", routeIdx: hIdx },
        ],
      );
    }
  }

  // Pilgrimage — "went ... and came home" presupposes a home here to come
  // back to, so it's capped by departure the same way as the rest.
  const pilgrimAge = departureYear != null ? Math.min(p.death.age, departureYear - p.birth) : p.death.age;
  if (pilgrimAge >= 28 && rng.chance(0.18)) {
    const y = p.birth + rng.int(25, Math.min(55, pilgrimAge - 1));
    const shrine = rng.pick(region.pilgrim)[locale];
    ev(
      y,
      ca
        ? `Va anar en pelegrinatge a ${shrine}, en compliment d'un vot, i va tornar amb una insígnia de pelegrí cosida al barret.`
        : `Went on pilgrimage to ${shrine}, in fulfilment of a vow, and came home with a pilgrim badge sewn to ${pos} hat.`,
      "life",
      cite("chron"),
    );
  }

  // Death — real cause from envelope, decoded detail.
  //
  // § accident: a hazardous/maritime/military trade pulls its detail from the
  // matching occupational-accident pool. Keyed off the "accident" cause now
  // rather than "disease": rollDeath adds those trades' hazard to the
  // ACCIDENT risk (mortality.ts), so the cause it produces is the one that
  // names them, and a man killed in the quarry is no longer filed as having
  // died of an illness. The military pool is deliberately an old wound or a
  // training mishap, never a battle death — an actual war-year kill reaches
  // the "war" cause on its own.
  //
  // § accident, children: a small child's fatal accidents were not an
  // adult's, and the coroners' rolls are emphatic about the difference — so
  // an accident under ten draws from its own pool.
  const riskPool =
    p.death.cause === "accident" && (p.riskTrade === "hazardous" || p.riskTrade === "maritime" || p.riskTrade === "military") && rng.chance(0.55)
      ? RISK_DEATH_DETAIL[locale][p.riskTrade]
      : null;
  const childAccident = p.death.cause === "accident" && p.death.age <= 9 ? CHILD_ACCIDENT_DETAIL[locale] : null;
  const deathPool = riskPool ?? childAccident ?? DEATH_DETAIL[locale][p.death.cause];
  // § named epidemics: the `disease` bucket is the last undifferentiated one
  // left, and it is the biggest. A death inside a dated outbreak's window —
  // or inside the endemic background of the region the person actually lived
  // in — is named instead of drawing another anonymous fever.
  //
  // Both the roll and the pick come off a side stream (hash.ts's
  // personStream), and `rng.pick(deathPool)` is still consumed exactly as
  // before whether or not an epidemic claims the death. That is deliberate:
  // it means adding this moved no draw in the main decode stream, so every
  // OTHER line of every existing biography is unchanged.
  //
  // § the epidemic year: a death whose CAUSE is now `epidemic` is not a
  // candidate for naming — it is already known which epidemic killed
  // {{him/her}}, because a dated outbreak's own hazard is what did it. So
  // that case looks the answer up rather than rolling for it, and only a
  // crisis-fever death with no named outbreak behind it falls through to
  // the DEATH_DETAIL.epidemic pool.
  const epiRng = makeRng(personStream(env.vHash, 41000, id));
  const epidemic =
    p.death.cause === "disease"
      ? epidemicAt(p.death.year, env.regionKey, p.death.age, epiRng)
      : p.death.cause === "epidemic"
        ? outbreakAt(p.death.year, env.regionKey, p.death.age)
        : null;
  const drawnDetail = rng.pick(deathPool);
  const dd = epidemic ? epiRng.pick(epidemic.detail[locale]) : drawnDetail;
  if (departureYear != null && destPerson && destUnion && destSpouse && destRecord) {
    // § departure: her real death IS recorded — in HER OWN destination
    // record (destPerson), a full independent life there (verified: as
    // rich as an ordinary native bio) — link straight to it, one hop
    // instead of the two (origin → husband's page → his wife listing) the
    // marriage-out event alone would otherwise leave as the only path.
    const destPlace = p.emigrateTo ? placeNameOf(env.worldSeed, p.emigrateTo, locale) : "";
    ev(
      p.death.year,
      ca
        ? `${p.name} ${p.surname} va morir l'any ${p.death.year}; la resta de la seva vida — el casament, els fills, i l'enterrament — queda escrita al registre de ${destPlace}.`
        : `${p.name} ${p.surname} died in ${p.death.year}; the rest of ${pos} life — the marriage, the children, and the burial — is written in the register of ${destPlace}.`,
      "elsewhere",
      cite("reg"),
      [nameRef(destPerson, destRecord)],
      deathDate,
    );
  } else if (departureYear != null) {
    // § departure, no destination record: a genuine dead end. Her death
    // itself is still real (rolled at the origin regardless of where she
    // ended up — see identity.ts), only the burial detail is unknowable
    // here, so keep the true cause and admit the rest honestly instead of
    // fabricating a local burial that didn't happen.
    ev(
      p.death.year,
      ca
        ? `${p.name} ${dd}. On va ser enterrat{{/da}}, aquest registre no ho diu.`
        : `${p.name} ${dd}. Where {{he/she}} was laid to rest, this register does not say.`,
      "elsewhere",
      cite("reg"),
      undefined,
      deathDate,
    );
  } else {
    let burialTxt: string;
    if (p.death.cause === "plague")
      burialTxt = ca
        ? "Enterrat{{/da}} de pressa, en una fossa compartida amb altres d'aquell temps de mort."
        : "Buried in haste, in a grave shared with others of that dying time.";
    else if (p.death.age === 0) burialTxt = "";
    else if (p.death.age < 12)
      burialTxt = ca
        ? "Enterrat{{/da}} al cementiri vora els altres fills de la família."
        : "Buried in the churchyard beside the other children of the family.";
    else if (wealth >= 4)
      burialTxt = ca
        ? "Enterrat{{/da}} a l'església parroquial davant l'altar, sota una llosa amb inscripció, amb misses fundades per la seva ànima."
        : "Buried in the parish church before the altar, beneath a marked stone, with masses endowed for the soul.";
    else burialTxt = rng.pick(BURIAL_PLAIN[locale]);
    if (motherVillage && p.death.cause !== "plague" && p.death.age > 0)
      burialTxt += ca
        ? ` Portat a ${motherVillage}, a la parròquia mare que serveix aquests pobles.`
        : ` Carried to ${motherVillage}, to the mother parish that serves these villages.`;
    // § accident / § violence: a coroner had to view every sudden death, and
    // his roll — not the parish register — is what recorded it. The cause
    // itself now says so, replacing the string-matched set of "which disease
    // entries were really accidents" this used to need.
    const coronersBusiness = p.death.cause === "accident" || p.death.cause === "violence";
    const dSrc = coronersBusiness ? cite("coroner") : cite("reg");
    ev(p.death.year, `${p.name} ${dd}. ${burialTxt}`, "death", dSrc, undefined, deathDate);
  }

  // § record scarcity: sometimes texture, world events, and marriage all
  // simply fail to roll — a genuine gap, not a bug, since the same is true
  // of real parish registers. Left silent it reads as a broken/empty
  // biography rather than the record scarcity it actually is. A longer
  // life had more chances to pick up entries, so "thin" is relative to
  // age, not a flat count: floor rises by one roughly every 20 years lived
  // (capped at +3), so this only flags genuine bottom-of-the-barrel luck,
  // not the ordinary short record of someone who simply died young — the
  // same move already made for founders (§ pre-register) and incomers (§
  // recorded elsewhere), extended to cover "recorded nowhere in between".
  // Never fires once departed: the "elsewhere" closing entry above already
  // explains the brevity — a second, different-voiced note saying the same
  // thing would just be noise.
  const thinFloor = 2 + Math.min(3, Math.floor(p.death.age / 20));
  if (departureYear == null && events.length <= thinFloor && p.death.age >= 15) {
    ev(
      p.birth + Math.ceil((p.death.age + 1) / 2),
      ca
        ? `El registre no diu res més de la seva vida entre l'entrada de naixement i la d'enterrament — un temps ordinari que ningú no va creure que calgués anotar.`
        : `The register says nothing more of ${pos} life between the entry of birth and the one of burial — an ordinary span nobody thought worth a line.`,
      "life",
      cite("reg"),
    );
  }

  // ---- appended passes (§ the Schism, § named epidemics, § the church's
  // own line) ----
  //
  // Deliberately placed HERE, after the last existing rng draw and after
  // the record-scarcity check, rather than anywhere more natural. Every
  // block above consumes `rng` in a fixed order, and the scarcity note
  // fires off `events.length`; inserting these earlier would have shifted
  // both, rewriting the texture of every biography in every world for the
  // sake of adding to it. Appended, they add entries and change nothing —
  // and events.sort() below puts them in their right year regardless of
  // the order they were pushed in.

  // § the Schism: papal news, gated exactly like the royal accessions —
  // and region-specific, because between 1378 and 1417 it genuinely was.
  for (const w of papalWorldEvents(locale)) {
    if (w[1] < p.birth + w[3] || w[0] > (departureYear ?? p.death.year)) continue;
    if (w[2] && !w[2].includes(env.regionKey)) continue;
    if (rng.chance(w[4])) ev(Math.max(w[0], p.birth + w[3]), w[7](p, locale, literate), w[5], SRC[locale][w[6] as DocumentKind]);
  }

  // § the harvest: the year the crop failed was the most memorable thing
  // that happened to most people in most decades, and the register had no
  // way to say so. Only the worst one of a life is narrated — a villager
  // who saw four bad years remembers the year of the great dearth, not a
  // list — and a documented failure is named, while an ordinary one is
  // simply a hungry year.
  {
    const lastYear = departureYear ?? p.death.year;
    let worstYear = 0;
    let worstYield = POOR_HARVEST;
    for (let y = Math.max(p.birth + 5, 1290); y <= lastYear; y++) {
      const h = harvestAt(env.worldSeed, env.regionKey, y);
      if (h < worstYield) {
        worstYield = h;
        worstYear = y;
      }
    }
    if (worstYear && rng.chance(worstYield < FAMINE ? 0.75 : 0.4)) {
      const named = namedDearthAt(env.regionKey, worstYear);
      const what = named ? named.name[locale] : ca ? "un any de fam" : "a year of hunger";
      const bought = wealth >= 3;
      ev(
        worstYear,
        ca
          ? `La collita va fallar — ${what}. ${
              bought
                ? "La casa va comprar gra a preu de fam i va passar l'any; als camins hi havia gent d'altres parròquies demanant pa."
                : "Es van menjar el gra de sembrar, i el que es va poder plegar dels marges. Els vells i els infants van ser els primers a caure."
            }`
          : `The harvest failed — ${what}. ${
              bought
                ? "The household bought grain at famine price and came through the year; there were folk from other parishes on the roads, begging bread."
                : "They ate the seed corn, and what could be gathered from the hedges. The old and the children went first."
            }`,
        "famine",
        cite(named ? "chron" : "reg"),
      );
    }
  }

  // § named epidemics: the dated outbreaks, as chronicle news for anyone
  // who lived through one — whether or not it was what killed them.
  for (const e of epidemicNews(locale)) {
    if (e.regions && !e.regions.includes(env.regionKey)) continue;
    const year = Math.max(e.from, p.birth + 6);
    if (year > Math.min(e.to, departureYear ?? p.death.year)) continue;
    if (rng.chance(0.4)) ev(year, e.text, "hardship", cite("chron"));
  }

  // § the church's own line: the parish had a priest, and who he was
  // changed — a new man instituted to the living was news of the same kind
  // as a new lord entering the manor, and the bishop's clerk wrote it down,
  // which is why we can know it at all.
  {
    const clergy = parishClergyOf(env.worldSeed, env.regionKey, env.villageIdx);
    const lastYear = departureYear ?? p.death.year;
    const vicar = clergy.title === "vicar";
    const saint = SAINTS[locale][clergy.patronSaintIdx % SAINTS[locale].length];

    // § the mortality of 1349, made legible. A living that fell vacant by
    // pestilence is the single most telling entry an institution register
    // holds, so it gets first refusal on this person's one clergy entry.
    const plagueVacancies = clergy.heads
      .map((h, idx) => ({ h, idx }))
      .filter(({ h }) => h.end === "plague" && h.vacated >= Math.max(p.birth + 6, 1292) && h.vacated <= lastYear);
    const institutions = institutionsBetween(clergy, Math.max(p.birth + 6, 1292), lastYear);

    if (plagueVacancies.length && rng.chance(0.55)) {
      const { h, idx } = rng.pick(plagueVacancies);
      const next = clergy.heads[idx + 1];
      // Where the successor was instituted in the same year and died in it
      // too, the register says so by simple arithmetic — no need to assert
      // the drama, only to count.
      const sameYear = clergy.heads.filter((o) => o.instituted === h.vacated).length;
      const run =
        sameYear >= 2
          ? ca
            ? ` Abans no s'acabà l'any n'hi hagué ${sameYear === 2 ? "dos més" : `${sameYear} més`} instituïts a la mateixa església.`
            : ` Before the year was out there were ${sameYear === 2 ? "two more" : `${sameYear} more`} instituted to the same church.`
          : "";
      ev(
        h.vacated,
        ca
          ? `${vicar ? "El vicari" : "El rector"} ${h.name} morí de la pestilència, havent enterrat mig poble abans que li arribés el torn.${next ? ` ${next.name} fou instituït al seu lloc.` : ""}${run}`
          : `${vicar ? "The vicar" : "The parson"}, ${h.name}, died of the pestilence, having buried half the village before his own turn came.${next ? ` ${next.name} was instituted in his place.` : ""}${run}`,
        "plague",
        cite("reg"),
        next ? [{ id: -1, name: next.name, addr: selfAddr, route: "rector", routeIdx: idx + 1 }] : undefined,
      );
    } else if (institutions.length && rng.chance(0.3)) {
      const { incumbent, idx } = rng.pick(institutions);
      const prev = clergy.heads[idx - 1];
      // § the appropriated living: who presents the man is the whole
      // difference between a rector and a vicar, so the entry names the
      // presenter rather than leaving the title unexplained.
      const patron = vicar
        ? ca
          ? `el priorat de ${saint}, que té les dècimes majors d'aquesta església`
          : `the priory of ${saint}, which holds the great tithes of this church`
        : lordOfManorAt(env.worldSeed, env.regionKey, env.villageIdx, incumbent.instituted).name;
      const gone = prev
        ? prev.end === "exchanged"
          ? ca
            ? ", que havia bescanviat el benefici per un altre més a prop de casa seva"
            : ", who had exchanged the benefice for another nearer his own home"
          : prev.end === "resigned"
            ? ca
              ? ", que hi havia renunciat"
              : ", who had resigned it"
            : ca
              ? ", mort"
              : ", dead"
        : "";
      ev(
        incumbent.instituted,
        ca
          ? `${incumbent.name} fou instituït ${vicar ? "vicari" : "rector"} d'aquesta església a presentació de ${patron}, al lloc de ${prev ? prev.name + gone : "l'anterior"}.`
          : `${incumbent.name} was instituted ${vicar ? "vicar" : "parson"} of this church at the presentation of ${patron}, in the place of ${prev ? prev.name + gone : "the last"}.`,
        "life",
        cite("reg"),
        [{ id: -1, name: incumbent.name, addr: selfAddr, route: "rector", routeIdx: idx }],
      );
    }
  }

  // Within a year: birth first, the closing entry last, everything else in
  // the order it was pushed. Expressed as a RANK rather than as a chain of
  // ternaries, which is what it used to be — and which was not a valid
  // comparator: compare("plague", "death") returned 0 while
  // compare("death", "plague") returned 1, so whether the closing entry
  // actually ended up last depended on insertion order and on the sort's
  // internals. It never showed while nothing was pushed after the death
  // entry; the appended passes above push plenty, and a parson dying of the
  // pestilence in the same year as the subject could land after the burial.
  const rank = (e: BioEvent): number => (e.kind === "birth" ? 0 : e.kind === "death" || e.kind === "elsewhere" ? 2 : 1);
  events.sort((a, b) => a.year - b.year || rank(a) - rank(b));

  // § nobility links: every sovereign named ANYWHERE in the chronicle —
  // accession news, war names, whatever prose mentions a king — links to
  // the region's royal line. Candidates are the reigns' styles, names, and
  // akas; all of them lead to the same royal-line view, so an ambiguous
  // "King Henry" can never link wrongly. Longest-match-first in the UI's
  // linkify keeps a person ref ("Lorenzo di Nardo") beating a shorter
  // royal candidate at the same position.
  addRoyalRefs(events, env.regionKey, locale, selfAddr);
  // § the Schism, links: same treatment for popes, resolved against THIS
  // region's own obedience so a mention in 1400 opens the pope this parish
  // was actually obeying and not the other one.
  addPapalRefs(events, env.regionKey, locale, selfAddr);

  const clergyLine = parishClergyOf(env.worldSeed, env.regionKey, env.villageIdx);
  const rectorIdx = incumbencyIndexAt(clergyLine.heads, p.birth);
  const birthTerm = popeTermAt(env.regionKey, p.birth);

  return {
    id,
    env,
    name: p.name,
    surname: p.surname,
    sex: p.sex,
    birth: p.birth,
    death: p.death,
    // § the season
    birthDate,
    deathDate,
    causeLabel: CAUSE_LABEL[locale][p.death.cause],
    cls: p.cls,
    clsLabel: CLASS_INFO[p.cls].label[locale],
    wealth,
    place: env.place[locale],
    region: region.name[locale],
    sovereign: sovereignAt(env.regionKey, p.birth)?.style[locale] ?? "",
    // § the Schism: empty where the region obeyed nobody, or the see stood
    // vacant, in the year this person was born — which is a real answer and
    // the page says so rather than hiding it.
    pontiff: birthTerm?.pope?.style[locale] ?? "",
    pontiffIdx: popeIndexAt(env.regionKey, p.birth),
    // § the church's own line: the man who would have baptised them.
    rector: clergyLine.heads[rectorIdx]?.name ?? "",
    rectorIdx,
    rectorTitle: clergyLine.title,
    literate,
    inOrders: !!p.inOrders,
    incomer: !!p.incomer,
    founder: !!p.founder,
    marriedOut: !!p.marriedOut,
    cityService: !!p.cityService,
    originPlace: p.incomer && p.origin ? placeNameOf(env.worldSeed, p.origin, locale) : null,
    jurisdiction,
    fief,
    father: father
      ? {
          id: father.id,
          name: father.name + " " + father.surname,
          dead: father.death,
          birth: father.birth,
          death: father.death,
          addr: fatherAddr,
          foreign: fatherAddr !== selfAddr,
        }
      : null,
    mother: mother
      ? {
          id: mother.id,
          name: mother.name + " " + mother.surname,
          dead: mother.death,
          birth: mother.birth,
          death: mother.death,
          addr: motherAddr,
          foreign: motherAddr !== selfAddr,
        }
      : null,
    fatherOccupation: fatherOccText,
    occupation,
    siblings: siblings.map((s) => ({ id: s.id, name: s.name, sex: s.sex, birth: s.birth, death: s.death, addr: selfAddr })),
    spouse:
      destSpouse && destRecord
        ? spouseRefOf(destSpouse, destRecord, env.worldSeed, locale)
        : spouse
          ? spouseRefOf(spouse, selfAddr, env.worldSeed, locale)
          : null,
    marriageYear: destUnion ? destUnion.year : own ? own.year : null,
    unions:
      destUnion && destSpouse && destRecord
        ? [unionRefOf(destUnion, destSpouse, destChildren, destRecord, env.worldSeed, locale)]
        : unionCouples.map((c) =>
            unionRefOf(
              c,
              spouseOf(c),
              c.children.map((cid) => env.persons[cid]),
              selfAddr,
              env.worldSeed,
              locale,
            ),
          ),
    children:
      destUnion && destRecord
        ? destChildren.map((c) => ({ id: c.id, name: c.name, sex: c.sex, birth: c.birth, death: c.death, addr: addrOnly(destRecord) }))
        : children.map((c) => ({ id: c.id, name: c.name, sex: c.sex, birth: c.birth, death: c.death, addr: selfAddr })),
    destRecord,
    events,
    widowed: widowedUnions.length,
    plaguesLived: PLAGUES.filter(
      (pl) => pl[0] >= p.birth && pl[1] <= p.death.year && !(p.death.cause === "plague" && p.death.year >= pl[0] && p.death.year <= pl[1]),
    ).length,
  };
}
